import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Hub as IntegrationsIcon,
} from '@mui/icons-material';
import settingsService from '../services/settingsService';
import SettingsProfileTab from './SettingsProfileTab';
import SettingsNotificationsTab from './SettingsNotificationsTab';
import SettingsIntegrationsTab from './SettingsIntegrationsTab';

const DEFAULT_NOTIFICATION_SETTINGS = {
  email_enabled: true,
  in_app_enabled: true,
  email_warning_days: [10, 7, 3],
  in_app_warning_days: [10, 7, 3],
  send_to_admin: true,
  send_to_client: false,
  email_subject: 'Υπενθύμιση Λήξης Υπηρεσίας - {client_name}',
  email_body_html: '',
};

const TAB_KEYS = ['profile', 'notifications', 'integrations'];

const SettingsModal = ({ open, onClose, initialTab = 'profile' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [useTestApi, setUseTestApi] = useState(true);
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATION_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open) {
      setActiveTab(TAB_KEYS.includes(initialTab) ? initialTab : 'profile');
      loadSettings();
    }
  }, [open, initialTab]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const [myDataResult, notifResult] = await Promise.all([
      settingsService.getMyDataSettings(),
      settingsService.getNotificationSettings(),
    ]);

    if (myDataResult.success) {
      setUseTestApi(myDataResult.data.use_test_api);
    } else {
      setError(myDataResult.error || 'Σφάλμα φόρτωσης ρυθμίσεων MyData');
    }

    if (notifResult.success) {
      setNotificationSettings({
        ...DEFAULT_NOTIFICATION_SETTINGS,
        ...notifResult.data,
      });
    } else if (!error) {
      setError(notifResult.error || 'Σφάλμα φόρτωσης ρυθμίσεων ειδοποιήσεων');
    }

    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    let result;
    if (activeTab === 'integrations') {
      result = await settingsService.updateMyDataSettings(useTestApi);
    } else if (activeTab === 'notifications') {
      result = await settingsService.updateNotificationSettings(notificationSettings);
    } else {
      setSaving(false);
      onClose();
      return;
    }

    if (result.success) {
      setSuccess('Οι ρυθμίσεις ενημερώθηκαν επιτυχώς');
      if (activeTab === 'notifications' && result.data) {
        setNotificationSettings({ ...DEFAULT_NOTIFICATION_SETTINGS, ...result.data });
      }
    } else {
      setError(result.error || 'Σφάλμα αποθήκευσης ρυθμίσεων');
    }

    setSaving(false);
  };

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    setError(null);
    setSuccess(null);
  };

  const showSaveButton = activeTab === 'notifications' || activeTab === 'integrations';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: { xs: '90vh', sm: '520px' },
          borderRadius: 2,
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">Ρυθμίσεις</Typography>
        </Box>
        <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5 }}>
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 0, sm: 1 } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              value="profile"
              label="Προφίλ"
              icon={<PersonIcon />}
              iconPosition="start"
              sx={{ minHeight: { xs: 48, sm: 64 } }}
            />
            <Tab
              value="notifications"
              label="Ειδοποιήσεις"
              icon={<NotificationsIcon />}
              iconPosition="start"
              sx={{ minHeight: { xs: 48, sm: 64 } }}
            />
            <Tab
              value="integrations"
              label="Διασυνδέσεις"
              icon={<IntegrationsIcon />}
              iconPosition="start"
              sx={{ minHeight: { xs: 48, sm: 64 } }}
            />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeTab === 'profile' && <SettingsProfileTab />}
              {activeTab === 'notifications' && (
                <>
                  {error && activeTab === 'notifications' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {success}
                    </Alert>
                  )}
                  <SettingsNotificationsTab
                    settings={notificationSettings}
                    onChange={setNotificationSettings}
                    disabled={saving}
                  />
                </>
              )}
              {activeTab === 'integrations' && (
                <SettingsIntegrationsTab
                  useTestApi={useTestApi}
                  onChange={setUseTestApi}
                  loading={false}
                  error={error}
                  success={success}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} disabled={saving}>
          {showSaveButton ? 'Ακύρωση' : 'Κλείσιμο'}
        </Button>
        {showSaveButton && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;
